import { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';

interface RevenueData {
  actualMRR: number;
  actualARR: number;
  theoreticalMRR: number;
  theoreticalARR: number;
  totalMRR: number; // For backward compatibility
  totalARR: number;
  activePaidCount: number;
  trialCount: number;
  revenueByTier: {
    tier1Count: number;
    tier2Count: number;
  };
  topTenants: Array<{
    tenantId: string;
    tenantName: string;
    licensedUserCount: number;
    mrr: number;
  }>;
}

export default function Billing({ adminUser }: any) {
  const [revenue, setRevenue] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRevenueData();
  }, []);

  const fetchRevenueData = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://admin.sign.virtue.dk';
      const response = await fetch(`${apiUrl}/api/billing/revenue-stats`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setRevenue(data.data);
      }
    } catch (error) {
      console.error('Error fetching revenue:', error);
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: 'DKK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <>
      <Navigation adminUser={adminUser} />
      <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto', background: '#f9fafb', minHeight: 'calc(100vh - 64px)' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', marginBottom: '32px' }}>
          Billing & Revenue
        </h1>

        {loading ? (
          <div style={{ color: '#666' }}>Loading revenue data...</div>
        ) : revenue ? (
          <>
            {/* Revenue KPI Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginBottom: '32px' }}>
              <div style={cardStyle}>
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Actual MRR (Stripe)</div>
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#10b981' }}>{formatCurrency(revenue.actualMRR)}</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>From active subscriptions</div>
              </div>

              <div style={cardStyle}>
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Actual ARR (Stripe)</div>
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#10b981' }}>{formatCurrency(revenue.actualARR)}</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>Annualized actual revenue</div>
              </div>

              <div style={cardStyle}>
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Theoretical MRR</div>
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#3b82f6' }}>{formatCurrency(revenue.theoreticalMRR)}</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>Based on licensed users</div>
              </div>

              <div style={cardStyle}>
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Paid Customers</div>
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#111827' }}>{revenue.activePaidCount}</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>Active subscriptions</div>
              </div>

              <div style={cardStyle}>
                <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Trial Customers</div>
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#111827' }}>{revenue.trialCount}</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>In trial period</div>
              </div>
            </div>

            {/* Revenue by Tier */}
            <div style={{ ...cardStyle, marginBottom: '32px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>
                Revenue by Tier
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
                <div>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Tier 1 (0-100 licensed users)</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>{revenue.revenueByTier.tier1Count}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>customers @ 100 DKK/month</div>
                </div>
                <div>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Tier 2 (101+ licensed users)</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>{revenue.revenueByTier.tier2Count}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>customers @ 1 DKK/licensed user/month</div>
                </div>
              </div>
            </div>

            {/* Top Customers by Revenue */}
            <div style={cardStyle}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>
                Top Customers by Revenue
              </h2>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                        Customer
                      </th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                        Licensed Users
                      </th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                        MRR
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenue.topTenants.map((tenant) => (
                      <tr key={tenant.tenantId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px 8px', color: '#111827', fontWeight: '500' }}>
                          {tenant.tenantName}
                        </td>
                        <td style={{ padding: '12px 8px', color: '#6b7280' }}>
                          {tenant.licensedUserCount}
                        </td>
                        <td style={{ padding: '12px 8px', color: '#111827', fontWeight: '500' }}>
                          {formatCurrency(tenant.mrr)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div style={{ color: '#ef4444' }}>Failed to load revenue data</div>
        )}
      </div>
    </>
  );
}
